import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Users, Trash2, Shield, ShieldOff, Crown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [invitedView, setInvitedView] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Account disabilitato" : "Account abilitato");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleVip = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_vip: !u.is_vip });
      toast.success(u.is_vip ? "Rimosso dal VIP" : "Abilitato al VIP 👑");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = (u) => { setEditing(u.id); setForm({ first_name: u.first_name, last_name: u.last_name, username: u.username, email: u.email }); };

  const setRole = async (u) => {
    const makeAdmin = u.role !== "admin";
    if (!window.confirm(makeAdmin ? `Nominare ${u.username} amministratore?` : `Rimuovere i permessi admin a ${u.username}?`)) return;
    try {
      await api.put(`/admin/users/${u.id}`, { role: makeAdmin ? "admin" : "user" });
      toast.success(makeAdmin ? "Nominato amministratore" : "Riportato a utente");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE ${u.username}? Azione irreversibile.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente eliminato"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    try {
      await api.put(`/admin/users/${editing}`, form);
      toast.success("Dati aggiornati"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (users === null) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  const filtered = users.filter((u) =>
    [u.username, u.email, u.first_name, u.last_name].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-extrabold uppercase text-zinc-950">Utenti</h2>
        <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="max-w-[200px] rounded-full" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-3">Utente</th>
              <th className="px-3 py-3">Nickname</th>
              <th className="px-3 py-3">Risposte</th>
              <th className="px-3 py-3">Accuratezza</th>
              <th className="px-3 py-3">Punti</th>
              <th className="px-3 py-3">Invitati</th>
              <th className="px-3 py-3">VIP</th>
              <th className="px-3 py-3">Attivo</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-slate-100">
                <td className="px-3 py-3">
                  <div className="font-semibold text-zinc-950">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-zinc-400">{u.email}</div>
                </td>
                <td className="px-3 py-3 font-semibold">{u.username}</td>
                <td className="px-3 py-3 font-mono-t">{u.answers_count}</td>
                <td className="px-3 py-3 font-mono-t">{u.accuracy}%</td>
                <td className="px-3 py-3 font-mono-t font-black text-green-600">{u.total_points}</td>
                <td className="px-3 py-3">
                  <button data-testid={`admin-user-invited-${u.id}`} onClick={() => setInvitedView(u)}
                    className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-zinc-700 hover:bg-green-100">
                    <Users className="h-3 w-3" /> {u.invited_count || 0}
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`admin-user-vip-${u.id}`} checked={!!u.is_vip} onCheckedChange={() => toggleVip(u)} />
                    {u.is_vip && <Crown className="h-4 w-4 text-yellow-500" />}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`admin-user-toggle-${u.id}`} checked={u.is_active} onCheckedChange={() => toggle(u)} />
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-zinc-500">{u.role}</span>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <Button onClick={() => openEdit(u)} size="sm" variant="ghost" title="Modifica"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`admin-user-role-${u.id}`} onClick={() => setRole(u)} size="sm" variant="ghost" title={u.role === "admin" ? "Rimuovi admin" : "Nomina admin"} className={u.role === "admin" ? "text-green-600" : "text-zinc-500"}>
                      {u.role === "admin" ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                    </Button>
                    <Button data-testid={`admin-user-delete-${u.id}`} onClick={() => removeUser(u)} size="sm" variant="ghost" className="text-red-500" title="Elimina utente"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">Modifica utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nome</Label><Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Cognome</Label><Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl" /></div>
            </div>
            <div className="space-y-1.5"><Label>Nickname</Label><Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" /></div>
            <p className="text-xs text-zinc-400">La password è sempre cifrata e non è mai visibile né modificabile da qui.</p>
          </div>
          <DialogFooter>
            <Button data-testid="admin-user-save" onClick={save} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!invitedView} onOpenChange={(v) => !v && setInvitedView(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">Amici invitati da {invitedView?.username}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Totale: {invitedView?.invited_count || 0} · Punti referral: {invitedView?.referral_points || 0}</p>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {(invitedView?.invited || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-zinc-400">Nessun amico invitato.</p>
            ) : invitedView.invited.map((f, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                <span className="font-semibold text-zinc-900">{f.username}</span>
                <span className="text-xs text-zinc-400">{f.created_at ? new Date(f.created_at).toLocaleDateString("it-IT") : ""}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
